import React, { useEffect, useMemo, useReducer } from 'react';
import { useLeafletContext } from '@react-leaflet/core';
import { contours as d3contours } from 'd3-contour';
import polygonClipping from 'polygon-clipping';
import * as L from 'leaflet';
import { assetUrl } from '../utils/asset';

// Blue -> Yellow -> Red color interpolation
function interpolateBlueYellowRed(t: number): string {
  // Clamp t to [0, 1]
  t = Math.max(0, Math.min(1, t));
  
  if (t < 0.5) {
    // Blue to Yellow
    const localT = t * 2;
    const r = Math.round(localT * 255);
    const g = Math.round(localT * 255);
    const b = Math.round((1 - localT) * 255);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Yellow to Red
    const localT = (t - 0.5) * 2;
    const r = 255;
    const g = Math.round((1 - localT) * 255);
    const b = 0;
    return `rgb(${r}, ${g}, ${b})`;
  }
}

type Mode = 'sm' | 'rain';

type Manifest = {
  time: string[];
  tiles: Array<{ latDeg?: number; lonDeg?: number; lat?: number; lon?: number; file?: string }>;
  meta?: { grid_step?: number };
};

type PolygonClippingPolygon = number[][][];
type PolygonClippingMultiPolygon = PolygonClippingPolygon[];

// Efficient single-day grid data types
type GridData = {
  date: string;
  lat: number[];
  lon: number[];
  values: number[];
  minVal: number;
  maxVal: number;
  mode: string;
  pointCount: number;
  gridSize: string;
};

// Performance constants
const ANIMATION_DEBOUNCE_MS = 50;
const ANIMATION_REDUCED_THRESHOLDS = 8;
const ANIMATION_NORMAL_THRESHOLDS = 15;
const MAX_CACHED_DAYS = 10;
const MAX_GRID_DIMENSION_STATIC = 95; // cap contour resolution for performance
const MAX_GRID_DIMENSION_ANIMATING = 70;

// LRU cache for memory management
class LRUCache {
  private cache: Map<string, GridData> = new Map();
  private maxSize: number;

  constructor(maxSize: number) {
    this.maxSize = maxSize;
  }

  get(key: string): GridData | undefined {
    const item = this.cache.get(key);
    if (item) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, item);
    }
    return item;
  }

  set(key: string, value: GridData): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Remove least recently used (first item)
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
        console.log(`🗑️ Evicted ${firstKey} from cache`);
      }
    }
    this.cache.set(key, value);
    console.log(`💾 Cached ${key} (${this.cache.size}/${this.maxSize})`);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

// Fetch single day data
// Generate date sequence for August-October 2024 (matching the daily grids)
function getDateFromIndex(dateIndex: number): string | null {
  const startDate = new Date('2024-08-01');
  const endDate = new Date('2024-10-31'); 
  
  const currentDate = new Date(startDate);
  currentDate.setDate(startDate.getDate() + dateIndex);
  
  if (currentDate > endDate) {
    return null;
  }
  
  return currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
}

async function fetchDayData(mode: Mode, date: string): Promise<GridData | null> {
  try {
    const modeUpper = mode.toUpperCase(); // Convert to uppercase for filename
    const response = await fetch(assetUrl(`/daily_grids/${modeUpper}_${date}.json`));
    if (!response.ok) {
      console.warn(`⚠️ No data for ${mode} ${date}`);
      return null;
    }
    const data = await response.json();
    console.log(`📅 Loaded ${mode} ${date}: ${data.pointCount} points, ${data.gridSize}`);
    return data;
  } catch (error) {
    console.error(`❌ Failed to fetch ${mode} ${date}:`, error);
    return null;
  }
}

interface Props {
  mode: Mode;
  dateIndex: number;
  manifest?: Manifest | null;
  visible: boolean;
  onDataRangeChange?: (mode: Mode, minVal: number, maxVal: number) => void;
  onLoadingChange?: (mode: Mode, isLoading: boolean) => void;
  indiaMask?: PolygonClippingMultiPolygon | null;
}

type BoundingBox = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

type Grid = {
  lat: number[];
  lon: number[];
  values: number[];
  minVal: number;
  maxVal: number;
};

function pointsEqual(a: number[], b: number[]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function simplifyRingForStride(ring: number[][], stride: number): number[][] {
  if (stride <= 1 || ring.length <= stride + 2) {
    return ring.map(([lon, lat]) => [lon, lat]);
  }
  const simplified: number[][] = [];
  for (let i = 0; i < ring.length - 1; i += stride) {
    const [lon, lat] = ring[i];
    simplified.push([lon, lat]);
  }
  const [lastLon, lastLat] = ring[ring.length - 1];
  if (!simplified.length || !pointsEqual(simplified[simplified.length - 1], [lastLon, lastLat])) {
    simplified.push([lastLon, lastLat]);
  }
  if (simplified.length < 4) {
    return ring.map(([lon, lat]) => [lon, lat]);
  }
  const [firstLon, firstLat] = simplified[0];
  const [closingLon, closingLat] = simplified[simplified.length - 1];
  if (!pointsEqual([firstLon, firstLat], [closingLon, closingLat])) {
    simplified.push([firstLon, firstLat]);
  }
  return simplified;
}

function simplifyMaskPolygons(mask: PolygonClippingMultiPolygon, stride: number): PolygonClippingMultiPolygon {
  if (stride <= 1) {
    return mask;
  }
  return mask.map(polygon =>
    polygon.map(ring => simplifyRingForStride(ring, stride))
  );
}

type MaskContainmentPolygon = {
  outer: number[][];
  holes: number[][][];
};

function pointInRing(point: number[], ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect = ((yi > point[1]) !== (yj > point[1])) &&
      (point[0] < (xj - xi) * (point[1] - yi) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInMask(point: number[], polygons: MaskContainmentPolygon[]): boolean {
  for (const polygon of polygons) {
    if (!polygon.outer || polygon.outer.length < 3) continue;
    if (pointInRing(point, polygon.outer)) {
      // Ensure the point is not inside any hole
      let insideHole = false;
      for (const hole of polygon.holes) {
        if (hole && hole.length >= 3 && pointInRing(point, hole)) {
          insideHole = true;
          break;
        }
      }
      if (!insideHole) {
        return true;
      }
    }
  }
  return false;
}

function isPolygonFullyInsideMask(poly: number[][][], polygons: MaskContainmentPolygon[]): boolean {
  if (!polygons.length) return false;
  for (const ring of poly) {
    for (let idx = 0; idx < ring.length; idx += Math.max(1, Math.floor(ring.length / 40))) {
      const point = ring[idx];
      if (!pointInMask(point, polygons)) {
        return false;
      }
    }
  }
  return true;
}

type State = {
  grid: Grid | null;
  loading: boolean;
};

type Action = 
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_GRID'; grid: Grid | null };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_GRID':
      return { ...state, grid: action.grid };
    default:
      return state;
  }
}

function ContourOverlay({ mode, dateIndex, manifest, visible, onDataRangeChange, onLoadingChange, indiaMask }: Props) {
  const context = useLeafletContext();
  const [state, dispatch] = useReducer(reducer, { grid: null, loading: false });
  const { grid, loading } = state;

  // Efficient LRU cache for daily data
  const dayCache = React.useRef<LRUCache>(new LRUCache(MAX_CACHED_DAYS));
  const layerRef = React.useRef<L.LayerGroup>(new L.LayerGroup());
  const maskSimplificationsRef = React.useRef<Map<number, PolygonClippingMultiPolygon>>(new Map());
  const maskContainmentRef = React.useRef<MaskContainmentPolygon[] | null>(null);

  // Animation state for performance optimization
  const isAnimatingRef = React.useRef(false);
  const lastRenderTimeRef = React.useRef(0);
  const renderDebounceRef = React.useRef<number | undefined>(undefined);
  const lastDateIndexRef = React.useRef(dateIndex);

  // Detect if we're likely in animation mode (rapid dateIndex changes)
  React.useEffect(() => {
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderTimeRef.current;
    
    // If dateIndex changed within 500ms, we're probably animating
    if (timeSinceLastRender < 500 && dateIndex !== lastDateIndexRef.current) {
      isAnimatingRef.current = true;
      
      // Clear animation flag after a delay
      if (renderDebounceRef.current) {
        clearTimeout(renderDebounceRef.current);
      }
      renderDebounceRef.current = window.setTimeout(() => {
        isAnimatingRef.current = false;
      }, 1000);
    }
    
    lastDateIndexRef.current = dateIndex;
    lastRenderTimeRef.current = now;
  }, [dateIndex]);

  // Clear cache when mode changes
  React.useEffect(() => {
    dayCache.current.clear();
    console.log(`🔄 Cleared cache for mode change: ${mode}`);
  }, [mode]);

  // Add layer to map
  useEffect(() => {
    const { map } = context;
    const layer = layerRef.current;

    // Ensure a dedicated pane for contours so we can control z-index
    const contourPaneName = 'contour-pane';
    if (!map.getPane(contourPaneName)) {
      map.createPane(contourPaneName);
    }
    const contourPane = map.getPane(contourPaneName);
    if (contourPane) {
      contourPane.style.zIndex = '450';
      contourPane.style.pointerEvents = 'none';
    }

    map.addLayer(layer);
    return () => {
      map.removeLayer(layer);
    };
  }, [context]);

  // Load data for current date
  useEffect(() => {
    // Debounce rapid changes during animation for better performance
    if (isAnimatingRef.current) {
      if (renderDebounceRef.current) {
        clearTimeout(renderDebounceRef.current);
      }
      renderDebounceRef.current = window.setTimeout(() => {
        loadDayData();
      }, ANIMATION_DEBOUNCE_MS);
      return;
    }

    // Immediate processing when not animating
    loadDayData();

    function loadDayData() {
      const date = manifest ? 
        (dateIndex >= 0 && dateIndex < manifest.time.length ? manifest.time[dateIndex] : null) :
        getDateFromIndex(dateIndex);
      
      if (!date) {
        dispatch({ type: 'SET_GRID', grid: null });
        onLoadingChange?.(mode, false);
        return;
      }
      const cacheKey = `${mode}_${date}`;

      // Check cache first
      const cached = dayCache.current.get(cacheKey);
      if (cached) {
        console.log(`⚡ Using cached data for ${cacheKey}`);
        const grid: Grid = {
          lat: cached.lat,
          lon: cached.lon,
          values: cached.values,
          minVal: cached.minVal,
          maxVal: cached.maxVal
        };
        dispatch({ type: 'SET_GRID', grid });
        onDataRangeChange?.(mode, cached.minVal, cached.maxVal);
        onLoadingChange?.(mode, false);
        return;
      }

      // Fetch new data
      dispatch({ type: 'SET_LOADING', loading: true });
      onLoadingChange?.(mode, true);

      console.log(`🔄 Fetching fresh data for ${cacheKey}, animated=${isAnimatingRef.current}`);

      fetchDayData(mode, date).then(data => {
        if (data) {
          // Cache the data
          dayCache.current.set(cacheKey, data);

          const grid: Grid = {
            lat: data.lat,
            lon: data.lon,
            values: data.values,
            minVal: data.minVal,
            maxVal: data.maxVal
          };
          
          dispatch({ type: 'SET_GRID', grid });
          onDataRangeChange?.(mode, data.minVal, data.maxVal);
        } else {
          dispatch({ type: 'SET_GRID', grid: null });
        }
        
        dispatch({ type: 'SET_LOADING', loading: false });
        onLoadingChange?.(mode, false);
      }).catch(error => {
        console.error(`❌ Error loading ${cacheKey}:`, error);
        dispatch({ type: 'SET_GRID', grid: null });
        dispatch({ type: 'SET_LOADING', loading: false });
        onLoadingChange?.(mode, false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, dateIndex, mode, onDataRangeChange, onLoadingChange]);

  useEffect(() => {
    maskSimplificationsRef.current.clear();
    maskContainmentRef.current = indiaMask ? simplifyMaskPolygons(indiaMask, 6).map(polygon => ({
      outer: polygon[0] ?? [],
      holes: polygon.length > 1 ? polygon.slice(1) : []
    })) : null;
  }, [indiaMask]);

  const maskBoundingBox = useMemo<BoundingBox | null>(() => {
    if (!indiaMask || !indiaMask.length) return null;
    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    for (const polygon of indiaMask) {
      for (const ring of polygon) {
        for (const [lon, lat] of ring) {
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
          if (lon < minLon) minLon = lon;
          if (lon > maxLon) maxLon = lon;
        }
      }
    }
    if (minLat === Infinity) return null;
    return { minLat, maxLat, minLon, maxLon };
  }, [indiaMask]);

  // Render contours into Leaflet layer as SVG paths clipped to India
  useEffect(() => {
    const layer = layerRef.current;
    console.log(`[ContourOverlay] Rendering: visible=${visible}, grid=${!!grid}, layer=${!!layer}, mode=${mode}`);
    if (!layer) return;
    layer.clearLayers();
    if (!visible || !grid) {
      console.log(`[ContourOverlay] Skipping render: visible=${visible}, grid=${!!grid}`);
      return;
    }
    const { lat, lon, values, minVal, maxVal } = grid;
    let workingLat = lat;
    let workingLon = lon;
    let workingValues = values;
    let workingMinVal = minVal;
    let workingMaxVal = maxVal;
    let workingNx = lon.length;
    let workingNy = lat.length;

    console.log(`[ContourOverlay] Grid dimensions: ${workingNy}x${workingNx}, non-NaN values: ${values.filter(v => !isNaN(v)).length}/${values.length}`);
    if (workingNx === 0 || workingNy === 0) return;

  const targetDimension = isAnimatingRef.current ? MAX_GRID_DIMENSION_ANIMATING : MAX_GRID_DIMENSION_STATIC;
  const downsampleFactor = Math.max(1, Math.ceil(Math.max(workingNx, workingNy) / targetDimension));
    if (downsampleFactor > 1) {
      const latIndices: number[] = [];
      const lonIndices: number[] = [];
      for (let i = 0; i < workingNy; i += downsampleFactor) latIndices.push(i);
      if (latIndices[latIndices.length - 1] !== workingNy - 1) latIndices.push(workingNy - 1);
      for (let j = 0; j < workingNx; j += downsampleFactor) lonIndices.push(j);
      if (lonIndices[lonIndices.length - 1] !== workingNx - 1) lonIndices.push(workingNx - 1);

      const sampledLat = latIndices.map(idx => workingLat[idx]);
      const sampledLon = lonIndices.map(idx => workingLon[idx]);
      const sampledValues: number[] = [];
      for (const yi of latIndices) {
        for (const xi of lonIndices) {
          sampledValues.push(workingValues[yi * workingNx + xi]);
        }
      }
      const validSamples = sampledValues.filter(v => Number.isFinite(v));
      if (validSamples.length) {
        workingMinVal = Math.min(...validSamples);
        workingMaxVal = Math.max(...validSamples);
      }
      workingLat = sampledLat;
      workingLon = sampledLon;
      workingValues = sampledValues;
      workingNy = sampledLat.length;
      workingNx = sampledLon.length;
      console.log(`[ContourOverlay] Downsampled grid for ${mode}: factor=${downsampleFactor}, dims=${workingNy}x${workingNx}`);
    }

    // Use adaptive thresholds based on animation state for better performance
    const baseThresholds = isAnimatingRef.current ? ANIMATION_REDUCED_THRESHOLDS : ANIMATION_NORMAL_THRESHOLDS;
    const numThresholds = downsampleFactor > 1 ? Math.max(6, Math.round(baseThresholds * 0.75)) : baseThresholds;
    const thresholds: number[] = [];
    for (let i = 1; i <= numThresholds; i++) {
      thresholds.push(workingMinVal + (i / (numThresholds + 1)) * (workingMaxVal - workingMinVal));
    }
    console.log(`[ContourOverlay] ${isAnimatingRef.current ? 'Reduced' : 'Normal'} thresholds for ${mode} (${numThresholds}):`, thresholds);

    const c = d3contours()
      .size([workingNx, workingNy])
      .smooth(true)
      .thresholds(thresholds)(workingValues);

    console.log(`[ContourOverlay] Generated ${c.length} contours for mode=${mode}, thresholds=${thresholds.length}`);

    // build SVG overlay per contour
    const latMin = workingLat[0], latMax = workingLat[workingLat.length-1];
    const lonMin = workingLon[0], lonMax = workingLon[workingLon.length-1];
    console.log(`[ContourOverlay] Grid bounds: lat=[${latMin}, ${latMax}], lon=[${lonMin}, ${lonMax}]`);
    console.log(`[ContourOverlay] Grid step calculation: latStep=${(latMax - latMin) / Math.max(1, workingNy - 1)}, lonStep=${(lonMax - lonMin) / Math.max(1, workingNx - 1)}`);

    const latStep = workingNy > 1 ? (latMax - latMin) / (workingNy - 1) : 0;
    const lonStep = workingNx > 1 ? (lonMax - lonMin) / (workingNx - 1) : 0;

    const maskForClipping = indiaMask && indiaMask.length ? indiaMask : null;
    let effectiveMask: PolygonClippingMultiPolygon | null = maskForClipping;
  let clippingMode: 'skip' | 'full' | 'simplified' = 'skip';
    let maskStride = 1;

    if (maskForClipping) {
      const needsSimplifiedMask = isAnimatingRef.current || downsampleFactor > 1;
      if (needsSimplifiedMask) {
        maskStride = Math.min(12, Math.max(2, Math.round(downsampleFactor * (isAnimatingRef.current ? 1.5 : 1))));
        if (!maskSimplificationsRef.current.has(maskStride)) {
          maskSimplificationsRef.current.set(maskStride, simplifyMaskPolygons(maskForClipping, maskStride));
        }
        effectiveMask = maskSimplificationsRef.current.get(maskStride) ?? maskForClipping;
        clippingMode = maskStride > 1 ? 'simplified' : 'full';
      } else {
        clippingMode = 'full';
      }
    }

    const clippingEnabled = !!effectiveMask;
    if (!clippingEnabled) {
      console.log('[ContourOverlay] Skipping polygon clipping (mask unavailable).');
    } else {
      console.log(`[ContourOverlay] Clipping mode for ${mode}: ${clippingMode}${clippingMode === 'simplified' ? ` (stride=${maskStride})` : ''}`);
    }
    
    c.forEach((contour, idx) => {
      try {
        const level = contour.value;
    const colorFn = interpolateBlueYellowRed;
    const range = workingMaxVal - workingMinVal;
    const color = colorFn(range === 0 ? 0.5 : (level - workingMinVal) / range);
        
        // Convert d3 contour coordinates to lat/lng
        const geoPolygons = contour.coordinates.map(polygon => 
          polygon.map(ring => {
            const converted = ring.map(([px, py]) => {
              const mappedLon = lonMin + px * lonStep;
              const mappedLat = latMin + py * latStep;
              return [Number(mappedLon.toFixed(6)), Number(mappedLat.toFixed(6))];
            });
            if (converted.length) {
              const [firstLon, firstLat] = converted[0];
              const [lastLon, lastLat] = converted[converted.length - 1];
              if (firstLon !== lastLon || firstLat !== lastLat) {
                converted.push([firstLon, firstLat]);
              }
            }
            return converted;
          })
        );

        // Clip each polygon to India boundaries
        const addPolygonToLayer = (rings: PolygonClippingPolygon) => {
          if (!rings || !rings.length) return;
          const latLngRings = rings
            .map(ring => ring.map(([lon, lat]) => [lat, lon] as [number, number]))
            .filter(ring => ring.length >= 3);
          if (!latLngRings.length) return;
          const polygon = L.polygon(latLngRings, {
            color: color,
            fillColor: color,
            fillOpacity: 0.6,
            weight: 0,
            stroke: false,
            pane: 'contour-pane',
            interactive: false
          });
          layer.addLayer(polygon);
        };

        geoPolygons.forEach(poly => {
          let needsClipping = clippingEnabled;
          if (needsClipping && maskContainmentRef.current) {
            const fullyInside = isPolygonFullyInsideMask(poly, maskContainmentRef.current);
            if (fullyInside) {
              needsClipping = false;
            }
          }

          if (needsClipping && clippingEnabled && effectiveMask) {
            try {
              if (maskBoundingBox) {
                let polyMinLat = Infinity, polyMaxLat = -Infinity, polyMinLon = Infinity, polyMaxLon = -Infinity;
                for (const ring of poly) {
                  for (const [lonVal, latVal] of ring) {
                    if (!Number.isFinite(latVal) || !Number.isFinite(lonVal)) continue;
                    if (latVal < polyMinLat) polyMinLat = latVal;
                    if (latVal > polyMaxLat) polyMaxLat = latVal;
                    if (lonVal < polyMinLon) polyMinLon = lonVal;
                    if (lonVal > polyMaxLon) polyMaxLon = lonVal;
                  }
                }
                if (
                  polyMaxLat < maskBoundingBox.minLat ||
                  polyMinLat > maskBoundingBox.maxLat ||
                  polyMaxLon < maskBoundingBox.minLon ||
                  polyMinLon > maskBoundingBox.maxLon
                ) {
                  return; // Skip polygons completely outside mask bounds
                }
              }
              const clipped = polygonClipping.intersection(poly as any, effectiveMask as any) as PolygonClippingMultiPolygon | null;
              if (clipped && clipped.length) {
                clipped.forEach(clippedPoly => addPolygonToLayer(clippedPoly));
              }
            } catch (clipError) {
              console.warn(`Clipping error for contour ${idx}:`, clipError);
              addPolygonToLayer(poly);
            }
          } else {
            addPolygonToLayer(poly);
          }
        });
      } catch (error) {
        console.warn(`Error processing contour ${idx}:`, error);
      }
    });

    console.log(`[ContourOverlay] Rendered ${c.length} contours for mode=${mode}`);
  }, [visible, grid, mode, indiaMask, maskBoundingBox]);

  // Show loading indicator in layer
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    if (loading && visible) {
      // Could add a loading spinner or overlay here
      console.log(`⏳ Loading ${mode} data...`);
    }
  }, [loading, visible, mode]);

  return null;
}

export default ContourOverlay;